import React, {useEffect, useState} from "react";
import {studentRegistrationApi} from "./studentRegistrationApi";
import {StudentRegistrationView} from "./StudentRegistrationView";
import {PageTitle} from "@components/Landmarks";
import {locations} from "@utilities/Location";
import {studentDetailsModel} from "../studentDetailsFactory";
import {studentApi} from "../studentApi";
import {isObjectNullOrEmpty} from "@utilities/Types/objectUtilities";
import {isNullOrUndefined} from "@utilities/commonUtilities";
import {studentModel} from "../StudentApproval/studentApprovalFactory";
import {NO_API_RESULTS} from "../studentConstants";
import {studentCensusApi} from "../studentCensusApi";
import {CENSUS_STATUS} from "../studentCensusConstants";
import {isFimcAdmin} from "@utilities/LocalStorage/storageUtilities";

interface StudentRegistrationContainerProps {
    districts: Record<string, any>[];
    handleApiCall: (...args: unknown[]) => void;
    isLoading: boolean;
    redirect: (path: string) => void;
    setWarnings: (val: string[]) => void;
    user: Record<string, any>;
    studentId?: string;
}

export const StudentRegistrationContainer: React.FC<StudentRegistrationContainerProps> = ({
                                                 handleApiCall,
                                                 isLoading,
                                                 setWarnings,
                                                 districts,
                                                 redirect,
                                                 user,
                                                 studentId
                                             }) => {

    const [studentDetails, setStudentDetails] = useState<Record<string, any> | null>(null);
    const [previousStudentDetails, setPreviousStudentDetails] = useState<Record<string, any> | null>(null);
    const [duplicateStudents, setDuplicateStudents] = useState<Record<string, any>[] | null>(NO_API_RESULTS);
    const [currentCensusStatus, setCurrentCensusStatus] = useState<string | null>(null);
    const [organizations, setOrganizations] = useState<string[] | null>(null);

    const isEditing = !isNullOrUndefined(studentId);
    const readOnly = currentCensusStatus === CENSUS_STATUS.CensusFimcAdminReview.id  && !isFimcAdmin();

    const handleRegisterStudent = async (registerModel: Record<string, unknown>) => {
        if (!isEditing)
            registerModel.userId = user.userId;

        const sanitizedModel = studentDetailsModel(registerModel);

        if (!isEditing)
            handleApiCall(() => studentRegistrationApi.attemptRegister(sanitizedModel), () => {
                redirect(locations.STUDENT_MANAGEMENT.path);
            });
        else {
            handleApiCall(() => studentRegistrationApi.updateStudent(sanitizedModel), () => {
                redirect(locations.STUDENT_MANAGEMENT.path);
            });
        }
    };

    const checkForDuplicateStudents = async (registerModel: Record<string, unknown>) => {
        setPreviousStudentDetails(registerModel);
        setDuplicateStudents(null);
        const sanitizedModel = studentDetailsModel(registerModel);

        handleApiCall(() => studentRegistrationApi.getDuplicateStudents(sanitizedModel), loadDuplicateStudents);
    }

    const loadDuplicateStudents = (result: Record<string, any>[] | null) => {
        if (result !== null)
            setDuplicateStudents(result.map(studentModel));
    }

    const loadStudentDetailsResult = (result: Record<string, unknown>) => {
        const studentDetails = studentDetailsModel({
            ...result,
            studentId: result.id,
            isEligible: true,
        });
        setStudentDetails(studentDetails);
        setPreviousStudentDetails(studentDetails);

    };

    const getOrganizations = () => {
        handleApiCall(() => studentApi.getOrganizations(), (result: string[]) => setOrganizations(result));
    };

    useEffect(() => {

       if(!organizations)
           getOrganizations();

        const loadStudentDetails = () => {
            handleApiCall(() => studentApi.getStudentDetails(studentId!), loadStudentDetailsResult)
        };

        handleApiCall(() => studentCensusApi.getCurrentCensusStatus(), (result: { toString: () => string }) =>
            setCurrentCensusStatus(result.toString()));

        if (isEditing) {
            setDuplicateStudents([]);
            loadStudentDetails();
        } else {
            setStudentDetails({ ...studentDetailsModel(), organization: user.organization });
            setPreviousStudentDetails(null);
        }

        // eslint-disable-next-line
    }, [studentId]);

    if (!districts || !user || isNullOrUndefined(user.userId))
        return null;

    return <>
        <PageTitle h1={`Register Student`}/>

        {!isObjectNullOrEmpty(studentDetails) &&
            <StudentRegistrationView
                districts={districts}
                duplicateStudents={duplicateStudents}
                handleRegisterStudent={handleRegisterStudent}
                isEditing={isEditing}
                isLoading={isLoading}
                checkForDuplicateStudents={checkForDuplicateStudents}
                organizations={organizations || []}
                patronDistrict={user.districtId}
                patronName={user.fullName}
                patronRole={user.patronRole}
                previousStudentDetails={previousStudentDetails}
                readOnly={readOnly}
                redirect={redirect}
                setWarnings={setWarnings}
                studentDetails={studentDetails!}
            />
        }
    </>;
};
